import {
  type ReportIssueCategory,
  reportIssueCategoryMapping,
  reportIssuePriorityMapping,
  type TReportIssueParams,
} from '@conversed/rest/src/contracts/llm-usages/report-issue';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { iconsHelper } from '../../utils/iconsHelper';
import FactCheckingAttachmentSection from '../FactCheckingAttachmentSection';

type Props = {
  reportIssueParams: TReportIssueParams;
  setReportIssueParams: (params: TReportIssueParams) => void;
};

export const ConversationReviewReportIssueForm: React.FC<Props> = ({
  reportIssueParams,
  setReportIssueParams,
}) => {
  const { t } = useTranslation();

  const toggleCategory = (category: ReportIssueCategory) => {
    const isAlreadySelected = reportIssueParams.categories.includes(category);
    const nextCategories = isAlreadySelected
      ? reportIssueParams.categories.filter((selected) => selected !== category)
      : [...reportIssueParams.categories, category];
    setReportIssueParams({
      ...reportIssueParams,
      categories: nextCategories,
    });
  };

  return (
    <div className='conversation-review-modal__report-issue-form'>
      {/* Categories — flex-wrap grid of chips, multi-select */}
      <div className='conversation-review-modal__report-issue-block'>
        <div className='conversation-review-modal__report-issue-block-label'>
          {t('train-ai.fact-checking.report-issue.category.label')}
          <span className='conversation-review-modal__report-issue-required'>
            {' *'}
          </span>
        </div>
        <div className='conversation-review-modal__report-issue-chips'>
          {reportIssueCategoryMapping.map(
            ({ key, labelTranslationKey, icon }) => {
              const isSelected = reportIssueParams.categories.includes(key);
              return (
                <label
                  key={key}
                  className={`conversation-review-modal__report-issue-chip ${
                    isSelected
                      ? 'conversation-review-modal__report-issue-chip--selected'
                      : ''
                  }`}>
                  <input
                    type='checkbox'
                    className='conversation-review-modal__report-issue-chip-checkbox'
                    checked={isSelected}
                    onChange={() => toggleCategory(key)}
                  />
                  <img
                    src={iconsHelper[icon]}
                    alt=''
                    className='conversation-review-modal__report-issue-chip-icon'
                  />
                  <span className='conversation-review-modal__report-issue-chip-label'>
                    {t(labelTranslationKey)}
                  </span>
                </label>
              );
            },
          )}
        </div>
      </div>

      {/* Priority — single-select pills */}
      <div className='conversation-review-modal__report-issue-block'>
        <div className='conversation-review-modal__report-issue-block-label'>
          {t('train-ai.fact-checking.report-issue.priority.label')}
        </div>
        <div className='conversation-review-modal__report-issue-pills'>
          {reportIssuePriorityMapping.map(
            ({ key, labelTranslationKey, icon }) => {
              const isSelected = reportIssueParams.priority === key;
              return (
                <label
                  key={key}
                  className={`conversation-review-modal__report-issue-pill ${
                    isSelected
                      ? 'conversation-review-modal__report-issue-pill--selected'
                      : ''
                  }`}>
                  <input
                    type='radio'
                    name='conversation-review-report-issue-priority'
                    className='conversation-review-modal__report-issue-pill-radio'
                    checked={isSelected}
                    onChange={() =>
                      setReportIssueParams({
                        ...reportIssueParams,
                        priority: key,
                      })
                    }
                  />
                  <img
                    src={iconsHelper[icon]}
                    alt=''
                    className='conversation-review-modal__report-issue-pill-icon'
                  />
                  <span className='conversation-review-modal__report-issue-pill-label'>
                    {t(labelTranslationKey)}
                  </span>
                </label>
              );
            },
          )}
        </div>
      </div>

      {/* Comment — single-line text */}
      <div className='conversation-review-modal__report-issue-block'>
        <div className='conversation-review-modal__report-issue-block-label'>
          {t('train-ai.fact-checking.report-issue.comment.label')}
        </div>
        <input
          type='text'
          className='conversation-review-modal__report-issue-comment'
          placeholder={t(
            'train-ai.fact-checking.report-issue.comment.placeholder',
          )}
          value={reportIssueParams.comment ?? ''}
          onChange={(event) =>
            setReportIssueParams({
              ...reportIssueParams,
              comment: event.target.value,
            })
          }
        />
      </div>

      {/* Attachment — reuse FactCheckingAttachmentSection (handles file upload logic);
          the surrounding white wrapper gives it the white-card look in the red form. */}
      <div className='conversation-review-modal__report-issue-block'>
        <div className='conversation-review-modal__report-issue-block-label'>
          {t('train-ai.fact-checking.report-issue.attachment.title')}
        </div>
        <div className='conversation-review-modal__report-issue-attachment'>
          <FactCheckingAttachmentSection
            reportIssueParams={reportIssueParams}
            setReportIssueParams={setReportIssueParams}
          />
        </div>
      </div>
    </div>
  );
};
